/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { generatePath, Link } from "react-router-dom";
import styled from "styled-components";
import {
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ROUTES } from "~/routing/routes";
import { MappedOrgUnitV2 } from "../types";

type Props = {
  orgUnit: MappedOrgUnitV2;
  onDetach: (item: MappedOrgUnitV2) => void;
};

export function OrgUnitTag({ orgUnit, onDetach }: Props) {
  return (
    <>
      <FlexContainer>
        <OrgUnitTagWrapper
          showBorder
          css={{
            padding: `calc(${theme.spacing.spacing02} - 1px) ${theme.spacing.spacing04}`,
          }}
        >
          <FlexContainer columnGap={theme.spacing.spacing02}>
            <div
              css={{
                width: "0.75rem",
                height: "0.75rem",
                backgroundColor: orgUnit.color ?? "#c0c0c0",
                borderRadius: theme.radius.sm,
              }}
            />
            <Link
              to={generatePath(ROUTES.businessContexts.details, {
                structureId: orgUnit.parentId,
                unitId: orgUnit.id,
              })}
            >
              <Text size="sm" mb={0}>
                {orgUnit.name}
              </Text>
            </Link>
          </FlexContainer>
        </OrgUnitTagWrapper>
        <OrgUnitTagWrapper
          css={{
            padding: `${theme.spacing.spacing01} ${theme.spacing.spacing03}`,
          }}
          onClick={() => onDetach(orgUnit)}
        >
          <ExtendedTooltip caption="Detach Environment">
            <SVGIcon name="brokenLink" size="sm" />
          </ExtendedTooltip>
        </OrgUnitTagWrapper>
      </FlexContainer>
    </>
  );
}

const OrgUnitTagWrapper = styled(FlexContainer)<{ showBorder?: boolean }>`
  background-color: ${theme.color.background.ui04};
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  border-right: ${({ showBorder = false }) =>
    showBorder ? `solid 1px ${theme.color.border.border02}` : undefined};

  svg {
    margin-top: -4px;
  }

  &:hover {
    background-color: ${theme.color.background.ui01};
  }
`;
